import { type RefObject, useEffect } from 'react';

import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { ArticleReference } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleReference';

interface SelectionHandlerProps {
    articleRef: RefObject<ArticleReference>;
    isMainCanvas: boolean;
    instanceSelectionIdsRef: RefObject<string[]>;
}

export const useSelectionHandler = ({ articleRef, isMainCanvas, instanceSelectionIdsRef }: SelectionHandlerProps) => {
    const {
        isDragNewArticleActiveRef,
        updateInstanceMatrices,
        registerArticleDragReference,
        unregisterArticleDragReference
    } = usePlacementStore.getState();

    useEffect(() => {
        const unsubscribe = usePlacementStore.subscribe(
            (state) => state.articleInstanceSelections,
            (newSelections) => {
                const instanceIdSet = new Set(Object.keys(articleRef.current.articleInstances));
                const prevArticleInstanceIds = instanceSelectionIdsRef.current.filter((instanceId) =>
                    instanceIdSet.has(instanceId)
                );

                const newArticleInstanceIds = Array.from(instanceIdSet).filter(
                    (instanceId) => instanceId in newSelections
                );

                instanceSelectionIdsRef.current = newArticleInstanceIds;

                if (isDragNewArticleActiveRef.current) {
                    return;
                }

                if (prevArticleInstanceIds.length > 0) {
                    const newArticleInstanceIdsSet = new Set(newArticleInstanceIds);
                    const deselectionIds = prevArticleInstanceIds.filter(
                        (instanceId) => !newArticleInstanceIdsSet.has(instanceId)
                    );
                    if (deselectionIds.length > 0) {
                        updateInstanceMatrices(false, articleRef, false, deselectionIds);
                    }
                }

                if (newArticleInstanceIds.length > 0) {
                    const currentState = usePlacementStore.getState();
                    const isDraggingBoxActive =
                        currentState.isAutoDragActiveRef.current &&
                        Object.keys(newSelections).length > 200 &&
                        currentState.draggingBoxRef.current.isActive;

                    if (!isDraggingBoxActive) {
                        updateInstanceMatrices(true, articleRef, false, newArticleInstanceIds);
                    }

                    if (isMainCanvas) {
                        registerArticleDragReference(
                            articleRef.current.id,
                            articleRef as RefObject<ArticleReference | undefined>
                        );
                    }
                } else if (prevArticleInstanceIds.length > 0 && newArticleInstanceIds.length === 0 && isMainCanvas) {
                    if (!usePlacementStore.getState().isBatchModeActive()) {
                        unregisterArticleDragReference(articleRef.current.id);
                    }
                }
            }
        );
        return () => unsubscribe();
    }, [
        articleRef,
        instanceSelectionIdsRef,
        isMainCanvas,
        isDragNewArticleActiveRef,
        registerArticleDragReference,
        unregisterArticleDragReference,
        updateInstanceMatrices
    ]);
};
